import React, { useEffect, useContext, useState } from 'react'
import htmlParser from 'html-react-parser'
import styled from 'styled-components'
import { PaymentElement } from '@stripe/react-stripe-js'
import { StripeElements } from '@stripe/stripe-js'
import { usePopperTooltip } from 'react-popper-tooltip'
import 'react-popper-tooltip/dist/styles.css'
import { useRouter } from 'next/router'

import { margin, marginSizes } from '@jarvis.oddle/core/theme/default'
import { P } from '@jarvis.oddle/core/components/Typo'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import IconLock from '@jarvis.oddle/core/components/Icons/IconLock'
import {
  InlineNotification,
  notificationStates
} from '@jarvis.oddle/core/components/InlineNotification'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Switch from '@jarvis.oddle/core/components/Switch'

import PaymentTypeCard from 'src/components/molecules/PaymentTypeCard'
import StripeElement from 'src/components/molecules/StripeElement'
import CreditCardInput from 'src/components/molecules/CreditCardInput'
import { paymentMethods, paymentTypes } from 'src/utils/payment'
import { CardHeader, CheckBox } from './Elements'
import Divider from '@jarvis.oddle/core/components/Divider'
import { SelectedSavedCreditCard } from 'src/components/molecules/CheckoutComponents'
import { isObjectEmpty } from 'src/utils/object'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import { darken, isLight, lighten } from 'src/utils/style'
import { selectThemeSettingData } from 'src/redux/customisation'
import { unselectSavedCreditCardAction } from 'src/redux/checkout'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { API_ROOT as DEFAULT_APP_API_ROOT, origin } from 'config/universal'
import { postLogApi } from '@jarvis.oddle/core/api/newrelic/client'
import { newrelicTrackUserAction } from 'src/utils/newrelicLogger'

const PaymentOptionsContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column'
}))`
  margin-bottom: ${margin[marginSizes.medium]};
`

const PaymentTypeContainer = styled(Div).attrs(() => ({
  marginBottom: `${marginSizes.small}`
}))``

const StyledInlineNotification = styled(InlineNotification)`
  margin-top: 25px;
  font-weight: 700;
`

const PaymentElementContainer = styled('div')`
  margin-bottom: 5px;
`

const PaymentElementSaveCard = styled(Flex)`
  background-color: ${(props) => props.backgroundColor};
  padding: 0 16px;
`

const StyledTooltipContainer = styled('div')`
  --tooltipBackground: #212b36;
  color: white;
  border-radius: 4px;
  padding: 1rem 1.5rem;
  width: 75%;
`

interface PaymentInputProps {
  selectedPaymentMethod: PaymentMethods
  onStripeLoaded: (x) => void
  onStripeReady: (x) => void
  isEnabledCustomerSaveCreditCardOnStripe: boolean
  selectedSavedCreditCard: Oms.V4.SelectedSavedCreditCard
}

const PaymentInput = ({
  selectedPaymentMethod,
  isEnabledCustomerSaveCreditCardOnStripe,
  selectedSavedCreditCard,
  ...props
}: PaymentInputProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const metaState = useAppSelector(selectDetailMenuSite)

  switch (selectedPaymentMethod?.type) {
    case paymentTypes.offline:
      return (
        selectedPaymentMethod.publicInformation && (
          <InlineNotification lightTheme state={notificationStates.informative}>
            {htmlParser(selectedPaymentMethod.publicInformation)}
          </InlineNotification>
        )
      )
    case paymentTypes.stripe:
      if (
        selectedPaymentMethod.stripeRequestPayment === true ||
        metaState?.features?.enableStripePaymentElement
      ) {
        return null
      }
      return (
        <React.Fragment>
          <StripeElement
            type={selectedPaymentMethod.paymentMethod}
            onStripeReady={props.onStripeReady}
            onStripeLoaded={props.onStripeLoaded}
            apiKey={selectedPaymentMethod.paymentProcessToken}
          />
        </React.Fragment>
      )
    case paymentTypes.stripeExpressAccount:
      if (
        selectedPaymentMethod.stripeRequestPayment === true ||
        metaState?.features?.enableStripePaymentElement
      ) {
        return null
      }
      if (
        isEnabledCustomerSaveCreditCardOnStripe &&
        !isObjectEmpty(selectedSavedCreditCard) &&
        selectedPaymentMethod.paymentMethod !== paymentMethods.FPX &&
        selectedPaymentMethod.stripeRequestPaymentButtonType !==
          paymentMethods.APPLE_PAY
      ) {
        return (
          <React.Fragment>
            <SelectedSavedCreditCard
              selectedSavedCreditCard={selectedSavedCreditCard}
            />
            <StyledInlineNotification
              lightTheme
              state={notificationStates.informative}
            >
              {t(
                'Your payment details are safe. All payment details are encrypted and securely processed in compliance with international payment card security standards. We do not have access to your payment details.'
              )}
            </StyledInlineNotification>
          </React.Fragment>
        )
      }
      return (
        <React.Fragment>
          <StripeElement
            type={selectedPaymentMethod.paymentMethod}
            onStripeReady={props.onStripeReady}
            onStripeLoaded={props.onStripeLoaded}
            apiKey={selectedPaymentMethod.paymentProcessToken}
          />
          {isEnabledCustomerSaveCreditCardOnStripe &&
            selectedPaymentMethod.paymentMethod !== paymentMethods.FPX &&
            selectedPaymentMethod.stripeRequestPaymentButtonType !==
              paymentMethods.APPLE_PAY && (
              <React.Fragment>
                <StyledInlineNotification
                  lightTheme
                  state={notificationStates.informative}
                >
                  {t(
                    'Your payment details are safe. All payment details are encrypted and securely processed in compliance with international payment card security standards. We do not have access to your payment details.'
                  )}
                </StyledInlineNotification>
                <CheckBox name='saveCreditCard' content={t('Save this Card')} />
                <Divider />
              </React.Fragment>
            )}
        </React.Fragment>
      )
    case paymentTypes.chinatrust:
      // react-credit-card-input is broken in dev mode
      if (selectedPaymentMethod.isEnabledChinatrustRedirect) {
        return null
      } else {
        return (
          <React.Fragment>
            <P bold>{t('Enter Your Payment Details')}</P>
            <CreditCardInput />
          </React.Fragment>
        )
      }
    default:
      return null
  }
}

interface PaymentMethods extends Oms.V4.PaymentMethodData {
  parsedId: string
}

interface PaymentDetailsProps {
  paymentMethods: PaymentMethods[]
  selectedPaymentMethod: PaymentMethods
  onSelectPaymentMethod: (x) => void
  onStripeLoaded?: (x) => void
  onStripeReady?: (x) => void
  isEnabledCustomerSaveCreditCardOnStripe: boolean
  selectedSavedCreditCard?: Oms.V4.SelectedSavedCreditCard
  elements?: StripeElements
}

class CheckoutErrorBoundary extends React.Component {
  componentDidCatch(error, errorInfo) {
    postLogApi({
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      payload: {
        message: `[FESC] Checkout error boundary caught error - Error: ${error.message}, Stack: ${errorInfo.componentStack}`
      }
    }).catch((e) => console.error(`[next_error_handling] ${e}`))
  }

  render() {
    return this.props.children
  }
}

const PaymentDetails = ({
  paymentMethods,
  selectedPaymentMethod,
  onSelectPaymentMethod,
  onStripeLoaded,
  onStripeReady,
  isEnabledCustomerSaveCreditCardOnStripe,
  selectedSavedCreditCard,
  elements
}: PaymentDetailsProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const { errors, setFieldValue, values, setFieldError } = useFormContext()
  const metaState = useAppSelector(selectDetailMenuSite)
  const router = useRouter()

  const paymentElement = elements?.getElement('payment')
  const { storeOrderId } = router.query as Record<string, string>

  const customisationState = useAppSelector(selectThemeSettingData)
  const dispatch = useAppDispatch()
  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible
  } = usePopperTooltip()

  const [showSavingToggle, setShowSavingToggle] = useState(false) // only for payment element
  const [iSelectingCreditCard, setISelectingCreditCard] = useState(true) // only for payment element
  const [showApplePayWarning, setApplePayWarning] = useState(false) // show warning for Apple Pay

  const {
    settings: { colorContainerColor, colorBodyFont }
  } = customisationState

  const hasStripePayment = paymentMethods.find((payment) =>
    [7, 20].includes(payment.type)
  )

  // show toggle for saving credit card at above of payment element
  const showSelectedSavedCreditCard =
    metaState?.features?.enableStripePaymentElement &&
    isEnabledCustomerSaveCreditCardOnStripe &&
    !isObjectEmpty(selectedSavedCreditCard) &&
    paymentMethods.some((payment) => payment.type === 20)

  const handleClickPaymentOption = (payment) => {
    newrelicTrackUserAction('checkout_select_card_input')
    if (metaState?.features?.enableStripePaymentElement && hasStripePayment) {
      paymentElement?.collapse()
      setShowSavingToggle(false)
      setISelectingCreditCard(false)
      dispatch(unselectSavedCreditCardAction())
      setFieldValue('saveCreditCard', false)
    }
    return onSelectPaymentMethod(payment)
  }

  // show the green tag at Selected Saved Credit Card to user know they are using saved card for payment element
  const handleSelectSavedCreditCard = () => {
    newrelicTrackUserAction('checkout_select_saved_card')
    if (metaState?.features?.enableStripePaymentElement && hasStripePayment) {
      paymentElement?.collapse()
      setShowSavingToggle(false)
      setISelectingCreditCard(true)
      setFieldValue('saveCreditCard', false)
    }
    onSelectPaymentMethod(paymentMethods.find((payment) => payment.type === 20))
  }

  useEffect(() => {
    if (metaState.features.enableStripePaymentElement) {
      postLogApi({
        origin,
        apiRoot: DEFAULT_APP_API_ROOT,
        payload: {
          message:
            `1 [FESC] Access checkout ${storeOrderId} at ${new Date()}`.substring(
              0,
              10000
            )
        }
      }).catch((e) => console.error(`[next_error_handling] ${e}`))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStripeIsReady = () => {
    postLogApi({
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      payload: {
        message:
          `2 [FESC] Rendered Payment Element for ${storeOrderId} at ${new Date()}`.substring(
            0,
            10000
          )
      }
    }).catch((e) => console.error(`[next_error_handling] ${e}`))
    onStripeReady(paymentElement)
    paymentElement?.on('change', (e) => {
      if (!e.collapsed) {
        newrelicTrackUserAction(
          'checkout_select_payment_method_payment_element',
          {
            payment_method: e.value.type
          }
        )
        setISelectingCreditCard(false)
        onStripeReady(paymentElement)
        setShowSavingToggle(e.value.type === 'card')
        onSelectPaymentMethod(hasStripePayment)
        dispatch(unselectSavedCreditCardAction())
        if (e.complete && e.value.type !== 'card') {
          setFieldValue('saveCreditCard', false)
        }
        setApplePayWarning(e.value.type === 'apple_pay')
      }
    })
  }

  return paymentMethods.length > 0 ? (
    <CheckoutErrorBoundary>
      <Div>
        <Flex
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <CardHeader>{t('Payment Details')}</CardHeader>
          <Flex flexDirection='row' alignItems='center' ref={setTriggerRef}>
            <IconLock />
            <P marginLeft={marginSizes.small}>{t('Secure checkout')}</P>
          </Flex>
          {visible && (
            <StyledTooltipContainer
              ref={setTooltipRef}
              {...getTooltipProps({ className: 'tooltip-container' })}
            >
              {t(
                'Your payment details are safe. All payment details are encrypted and securely processed in compliance with international payment card security standards. We do not have access to your payment details.'
              )}
              <div {...getArrowProps({ className: 'tooltip-arrow' })} />
            </StyledTooltipContainer>
          )}
        </Flex>
        {showSelectedSavedCreditCard && (
          <SelectedSavedCreditCard
            selectedSavedCreditCard={selectedSavedCreditCard}
            iSelected={iSelectingCreditCard}
            onSelectSavedCreditCard={handleSelectSavedCreditCard}
            showRadioButton
          />
        )}
        <P bold>
          {showSelectedSavedCreditCard
            ? t('More Payment Methods')
            : t('Select a Payment Method')}
        </P>
        <PaymentOptionsContainer>
          {metaState?.features?.enableStripePaymentElement && hasStripePayment && (
            <PaymentElementContainer>
              {showSavingToggle && isEnabledCustomerSaveCreditCardOnStripe && (
                <PaymentElementSaveCard
                  flexDirection='row'
                  alignItems='center'
                  justifyContent='space-between'
                  backgroundColor={
                    isLight(colorContainerColor)
                      ? darken(colorContainerColor, 10)
                      : lighten(colorContainerColor, 15)
                  }
                >
                  <P color={colorBodyFont}>
                    {t(
                      "Save card for faster checkout across any of Oddle's restaurant partners."
                    )}
                  </P>
                  <Switch
                    name='saveCreditCard'
                    onChange={() =>
                      setFieldValue('saveCreditCard', !values.saveCreditCard)
                    }
                    checked={values.saveCreditCard}
                    controlled
                  />
                </PaymentElementSaveCard>
              )}
              <PaymentElement
                onReady={handleStripeIsReady}
                options={{
                  layout: {
                    type: 'accordion',
                    spacedAccordionItems: true,
                    defaultCollapsed: true,
                    radios: true
                  },
                  wallets: {
                    applePay: 'auto',
                    googlePay: 'auto'
                  },
                  fields: {
                    billingDetails: {
                      address: {
                        country: 'never'
                      }
                    }
                  }
                }}
                onLoadError={(event) => {
                  if (event?.error?.message && setFieldError) {
                    setFieldError('serverError', [event.error.message])
                  }
                  postLogApi({
                    origin,
                    apiRoot: DEFAULT_APP_API_ROOT,
                    payload: {
                      message:
                        `[FESC] Stripe payment element error ${JSON.stringify(
                          event.error
                        )}`.substring(0, 10000)
                    }
                  }).catch((e) => console.error(`[next_error_handling] ${e}`))
                }}
              />
            </PaymentElementContainer>
          )}
          {paymentMethods
            .filter((payment) => {
              if (metaState?.features?.enableStripePaymentElement) {
                return ![7, 20].includes(payment.type)
              }
              return true
            })
            .map((payment, idx) => (
              <PaymentTypeContainer key={idx}>
                <PaymentTypeCard
                  payment={payment}
                  selected={
                    selectedPaymentMethod?.parsedId === payment.parsedId
                  }
                  onClick={handleClickPaymentOption}
                />
              </PaymentTypeContainer>
            ))}
          {errors.paymentMethod && (
            <Form.FieldError>{errors.paymentMethod}</Form.FieldError>
          )}
        </PaymentOptionsContainer>
        <PaymentInput
          selectedPaymentMethod={selectedPaymentMethod}
          onStripeReady={onStripeReady}
          onStripeLoaded={onStripeLoaded}
          isEnabledCustomerSaveCreditCardOnStripe={
            isEnabledCustomerSaveCreditCardOnStripe
          }
          selectedSavedCreditCard={selectedSavedCreditCard}
        />
        {showApplePayWarning && (
          <StyledInlineNotification
            lightTheme
            state={notificationStates.informative}
          >
            {t(
              'Oddle Eats bank vouchers will not be applied if payment is made via Apple Pay'
            )}
          </StyledInlineNotification>
        )}
      </Div>
    </CheckoutErrorBoundary>
  ) : null
}

export default PaymentDetails
